import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, X, ChevronDown, ChevronRight, Search, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Éditeur d'inventaire d'échafauds — version simplifiée :
 *   1. Inventaire de pièces (groupes repliables + recherche + catégorie)
 *   2. Templates d'étages (composition + add-ons premier/dernier/accès)
 *   3. Règles de sécurité (fixations + équerres auto par distance)
 *
 * Props:
 *   unite: objet éditable (editUnite) — contient inventaire_pieces, types_etages, regles_securite
 *   onChange: (patch) => void — setter partiel de l'unite
 */

const CATEGORIES = [
  { id: 'cadres', label: '📦 Cadres' },
  { id: 'croisillons', label: '✖ Croisillons' },
  { id: 'madriers', label: '📏 Madriers / Planches' },
  { id: 'equerres', label: '📐 Équerres' },
  { id: 'fixations', label: '🔗 Fixations & ancrages' },
  { id: 'garde_corps', label: '🛡 Garde-corps & plinthes' },
  { id: 'acces', label: '🪜 Échelles & accès' },
  { id: 'accessoires', label: '🔩 Accessoires' },
];

export function EchafaudsInventaireUI({ unite, onChange }) {
  const [search, setSearch] = useState('');
  const [openCats, setOpenCats] = useState({ cadres: true });

  const pieces = unite.inventaire_pieces || [];
  const templates = unite.types_etages || [];
  const regles = unite.regles_securite || {};

  // Grouper les pièces par catégorie + filtrage par recherche
  const groupedPieces = useMemo(() => {
    const q = search.trim().toLowerCase();
    const groups = {};
    pieces.forEach((p, originalIdx) => {
      if (q && !(p.nom || '').toLowerCase().includes(q) && !(p.categorie || '').toLowerCase().includes(q)) return;
      const cat = p.categorie || 'accessoires';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push({ ...p, __idx: originalIdx });
    });
    return groups;
  }, [pieces, search]);

  const updatePieces = (next) => onChange({ inventaire_pieces: next });
  const updateTemplates = (next) => onChange({ types_etages: next });
  const updateRegles = (patch) => onChange({ regles_securite: { ...regles, ...patch } });

  const addPiece = (categorie = 'accessoires') => {
    updatePieces([
      ...pieces,
      { nom: '', categorie, quantite_stock: 0, unite: 'unité', prix_jour: 0, prix_semaine: 0, prix_mois: 0 },
    ]);
    // Ouvrir automatiquement la catégorie où on vient d'ajouter
    setOpenCats((s) => ({ ...s, [categorie]: true }));
  };

  const patchPiece = (idx, patch) => {
    const next = [...pieces];
    next[idx] = { ...next[idx], ...patch };
    updatePieces(next);
  };

  const removePiece = (idx) => {
    updatePieces(pieces.filter((_, i) => i !== idx));
  };

  const addTemplate = () => {
    updateTemplates([
      ...templates,
      {
        id: `type_${Date.now()}`,
        type: '',
        dimensions: '',
        quantite_disponible: 0,
        composition: [],
        addon_premier: [],
        addon_dernier: [],
        addon_acces: [],
      },
    ]);
  };

  const patchTemplate = (idx, patch) => {
    const next = [...templates];
    next[idx] = { ...next[idx], ...patch };
    updateTemplates(next);
  };

  const removeTemplate = (idx) => {
    updateTemplates(templates.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-4 border border-orange-200 rounded-lg p-4 bg-orange-50/30">
      {/* ═══════════════ 1. INVENTAIRE ═══════════════ */}
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-sm font-bold text-orange-800">🔩 Inventaire de pièces (Stock réel)</p>
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                if (!unite.id) {
                  toast.error("Enregistrez d'abord la remorque avant d'ajouter les pièces standard");
                  return;
                }
                try {
                  const res = await axios.post(`${API_URL}/api/unites-rangement/${unite.id}/seed-pieces-standard`);
                  const { ajoutees, deja_presentes, inventaire_pieces } = res.data;
                  if (ajoutees?.length > 0) {
                    toast.success(`${ajoutees.length} pièce(s) standard ajoutée(s) : ${ajoutees.join(', ')}`);
                    onChange({ inventaire_pieces });
                  } else if (deja_presentes?.length > 0) {
                    toast.info('Toutes les pièces standard sont déjà présentes');
                  }
                } catch (e) {
                  toast.error('Erreur : ' + (e?.response?.data?.detail || e.message));
                }
              }}
              className="h-7 text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
              data-testid="btn-seed-standard"
            >
              <Sparkles className="w-3 h-3 mr-1" /> Pièces standard
            </Button>
            <Button size="sm" variant="outline" onClick={() => addPiece('accessoires')} className="h-7 text-xs border-orange-300 hover:bg-orange-100" data-testid="btn-add-piece" title="Ajoute une pièce dans Accessoires (utilisez le « + » d'une catégorie pour cibler)">
              <Plus className="w-3 h-3 mr-1" /> Ajouter une pièce
            </Button>
          </div>
        </div>

        {/* Barre de recherche */}
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
          <Input
            placeholder="Rechercher une pièce..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 text-xs pl-7"
            data-testid="inventaire-search"
          />
        </div>

        {pieces.length === 0 ? (
          <p className="text-xs text-orange-600 font-semibold bg-orange-100 p-3 rounded border border-orange-300">
            ⚠️ Ajoutez vos pièces — cliquez sur ✨ "Pièces standard" pour démarrer rapidement, ou sur le bouton « + » d'une catégorie pour ajouter manuellement.
          </p>
        ) : (
          <div className="space-y-1">
            {CATEGORIES.map((cat) => {
              const lst = groupedPieces[cat.id] || [];
              if (lst.length === 0 && search) return null;
              const open = openCats[cat.id];
              return (
                <div key={cat.id} className="bg-white border border-orange-200 rounded overflow-hidden">
                  <div className="w-full flex items-center justify-between px-2 py-1.5 hover:bg-orange-50 group">
                    <button
                      type="button"
                      onClick={() => setOpenCats((s) => ({ ...s, [cat.id]: !s[cat.id] }))}
                      className="flex-1 flex items-center gap-2 text-left text-xs font-semibold text-orange-900"
                      data-testid={`cat-toggle-${cat.id}`}
                    >
                      {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                      {cat.label}
                      <span className="text-slate-400 font-normal">({lst.length})</span>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); addPiece(cat.id); }}
                      className="ml-2 p-1 rounded hover:bg-orange-200 text-orange-700"
                      title={`Ajouter une pièce dans ${cat.label}`}
                      data-testid={`btn-add-piece-${cat.id}`}
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {(open || search) && (
                    <div className="p-2 space-y-1 border-t border-orange-100">
                      {lst.length === 0 ? (
                        <button
                          type="button"
                          onClick={() => addPiece(cat.id)}
                          className="w-full text-xs text-orange-600 italic hover:bg-orange-50 hover:text-orange-800 py-2 px-2 rounded border border-dashed border-orange-300 transition"
                          data-testid={`btn-add-empty-${cat.id}`}
                        >
                          Aucune pièce dans cette catégorie. Cliquez ici pour en ajouter une.
                        </button>
                      ) : (
                        lst.map((piece) => (
                          <PieceRow
                            key={piece.__idx}
                            piece={piece}
                            onChange={(patch) => patchPiece(piece.__idx, patch)}
                            onDelete={() => removePiece(piece.__idx)}
                            allPieces={pieces}
                          />
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ═══════════════ 2. TEMPLATES D'ÉTAGES ═══════════════ */}
      <div className="space-y-2 pt-3 border-t-2 border-orange-300">
        <div className="flex items-center justify-between">
          <p className="text-sm font-bold text-orange-800">📐 Types d'étages (Templates)</p>
          <Button size="sm" variant="outline" onClick={addTemplate} className="h-7 text-xs border-orange-300 hover:bg-orange-100" data-testid="btn-add-template">
            <Plus className="w-3 h-3 mr-1" /> Ajouter un type
          </Button>
        </div>
        {templates.length === 0 ? (
          <p className="text-xs text-orange-600 italic">Aucun type d'étage défini. Exemple : "Standard 5×5".</p>
        ) : (
          <div className="space-y-3">
            {templates.map((tpl, idx) => (
              <TemplateRow
                key={tpl.id || idx}
                template={tpl}
                pieces={pieces}
                onChange={(patch) => patchTemplate(idx, patch)}
                onDelete={() => removeTemplate(idx)}
              />
            ))}
          </div>
        )}
      </div>

      {/* ═══════════════ 3. RÈGLES DE SÉCURITÉ ═══════════════ */}
      <div className="space-y-2 pt-3 border-t-2 border-orange-300">
        <p className="text-sm font-bold text-orange-800">⚠️ Règles de sécurité</p>
        <div className="bg-white border border-orange-200 rounded p-3 space-y-3 text-xs">
          {/* Fixations murales */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
            <div>
              <label className="text-[11px] text-slate-600 font-medium">Fixation murale obligatoire dès</label>
              <Input
                type="number"
                min="1"
                max="10"
                value={regles.fixation_obligatoire_a_partir_de || 3}
                onChange={(e) => updateRegles({ fixation_obligatoire_a_partir_de: parseInt(e.target.value, 10) || 3 })}
                className="h-8 text-xs mt-0.5"
                data-testid="regle-fix-seuil"
              />
              <p className="text-[10px] text-slate-400 mt-0.5">étages de haut</p>
            </div>
            <div>
              <label className="text-[11px] text-slate-600 font-medium">Min fixations/tour</label>
              <Input
                type="number"
                min="1"
                max="10"
                value={regles.fixations_min_par_tour || 1}
                onChange={(e) => updateRegles({ fixations_min_par_tour: parseInt(e.target.value, 10) || 1 })}
                className="h-8 text-xs mt-0.5"
              />
            </div>
            <div>
              <label className="text-[11px] text-slate-600 font-medium">Max fixations/tour</label>
              <Input
                type="number"
                min="1"
                max="10"
                value={regles.fixations_max_par_tour || 2}
                onChange={(e) => updateRegles({ fixations_max_par_tour: parseInt(e.target.value, 10) || 2 })}
                className="h-8 text-xs mt-0.5"
              />
            </div>
          </div>
          <div>
            <label className="text-[11px] text-slate-600 font-medium">Pièce utilisée pour fixation murale</label>
            <Select
              value={regles.fixation_piece_id || '__empty__'}
              onValueChange={(v) => updateRegles({ fixation_piece_id: v === '__empty__' ? null : v })}
            >
              <SelectTrigger className="h-8 text-xs mt-0.5">
                <SelectValue placeholder="Sélectionner..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__empty__">— Aucune —</SelectItem>
                {pieces.filter((p) => p.nom).map((p, i) => (
                  <SelectItem key={i} value={p.nom}>{p.nom}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Équerres par distance */}
          <div className="pt-2 border-t border-orange-100">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[11px] font-semibold text-orange-800">📐 Équerres automatiques par distance au mur</p>
              <Button
                size="sm"
                variant="outline"
                className="h-6 text-[10px] border-orange-200"
                onClick={() => {
                  const curr = regles.equerres_par_distance || [];
                  updateRegles({ equerres_par_distance: [...curr, { min_inch: 0, max_inch: 0, piece_id: '', nom: '' }] });
                }}
              >
                <Plus className="w-2.5 h-2.5 mr-0.5" /> Règle
              </Button>
            </div>
            {(regles.equerres_par_distance || []).length === 0 ? (
              <p className="text-[11px] text-slate-400 italic">Ex : 35–45″ du mur → Équerre 30″</p>
            ) : (
              <div className="space-y-1">
                {(regles.equerres_par_distance || []).map((r, ridx) => (
                  <div key={ridx} className="flex items-center gap-1.5">
                    <Input
                      type="number"
                      min="0"
                      max="120"
                      placeholder="Min ″"
                      value={r.min_inch || ''}
                      onChange={(e) => {
                        const next = [...(regles.equerres_par_distance || [])];
                        next[ridx] = { ...next[ridx], min_inch: parseInt(e.target.value, 10) || 0 };
                        updateRegles({ equerres_par_distance: next });
                      }}
                      className="h-7 text-xs w-16"
                    />
                    <span className="text-[10px] text-slate-400">→</span>
                    <Input
                      type="number"
                      min="0"
                      max="120"
                      placeholder="Max ″"
                      value={r.max_inch || ''}
                      onChange={(e) => {
                        const next = [...(regles.equerres_par_distance || [])];
                        next[ridx] = { ...next[ridx], max_inch: parseInt(e.target.value, 10) || 0 };
                        updateRegles({ equerres_par_distance: next });
                      }}
                      className="h-7 text-xs w-16"
                    />
                    <Select
                      value={r.piece_id || '__empty__'}
                      onValueChange={(v) => {
                        const next = [...(regles.equerres_par_distance || [])];
                        const piece = pieces.find((p) => p.nom === v);
                        next[ridx] = { ...next[ridx], piece_id: v === '__empty__' ? '' : v, nom: piece?.nom || v };
                        updateRegles({ equerres_par_distance: next });
                      }}
                    >
                      <SelectTrigger className="h-7 text-xs flex-1">
                        <SelectValue placeholder="Équerre..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__empty__">— Choisir —</SelectItem>
                        {pieces.filter((p) => p.categorie === 'equerres' && p.nom).map((p, i) => (
                          <SelectItem key={i} value={p.nom}>{p.nom}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-red-400"
                      onClick={() => {
                        const next = (regles.equerres_par_distance || []).filter((_, i) => i !== ridx);
                        updateRegles({ equerres_par_distance: next });
                      }}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Ligne d'une pièce ──────────────────────────────────────────────
function PieceRow({ piece, onChange, onDelete, allPieces }) {
  const substituts = allPieces.filter((p) => p.nom && p.nom !== piece.nom);
  return (
    <div className="bg-orange-50/50 p-2 rounded border border-orange-100 space-y-1">
      <div className="grid grid-cols-12 gap-1.5 items-end">
        <div className="col-span-5">
          <label className="text-[10px] text-slate-500">Nom</label>
          <Input value={piece.nom || ''} onChange={(e) => onChange({ nom: e.target.value })}
            placeholder="Cadre 5×5" className="h-7 text-xs mt-0" />
        </div>
        <div className="col-span-3">
          <label className="text-[10px] text-slate-500">Catégorie</label>
          <Select value={piece.categorie || 'accessoires'} onValueChange={(v) => onChange({ categorie: v })}>
            <SelectTrigger className="h-7 text-xs mt-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2">
          <label className="text-[10px] text-slate-500">Stock</label>
          <Input type="number" min="0" value={piece.quantite_stock === 0 ? '' : (piece.quantite_stock || '')}
            onChange={(e) => onChange({ quantite_stock: parseInt(e.target.value, 10) || 0 })}
            className="h-7 text-xs mt-0" />
        </div>
        <div className="col-span-1">
          <label className="text-[10px] text-slate-500">Unité</label>
          <Input value={piece.unite || 'unité'} onChange={(e) => onChange({ unite: e.target.value })}
            className="h-7 text-xs mt-0" />
        </div>
        <div className="col-span-1 flex justify-end">
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={onDelete}>
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>
      {/* Prix compact + substitut terrain pente */}
      <div className="grid grid-cols-12 gap-1.5">
        <div className="col-span-2">
          <label className="text-[10px] text-slate-400">$/jour</label>
          <Input type="number" min="0" step="0.01" value={piece.prix_jour ?? ''}
            onChange={(e) => onChange({ prix_jour: parseFloat(e.target.value) || 0 })}
            placeholder="0" className="h-7 text-[11px] mt-0" />
        </div>
        <div className="col-span-2">
          <label className="text-[10px] text-slate-400">$/sem</label>
          <Input type="number" min="0" step="0.01" value={piece.prix_semaine ?? ''}
            onChange={(e) => onChange({ prix_semaine: parseFloat(e.target.value) || 0 })}
            placeholder="0" className="h-7 text-[11px] mt-0" />
        </div>
        <div className="col-span-2">
          <label className="text-[10px] text-slate-400">$/mois</label>
          <Input type="number" min="0" step="0.01" value={piece.prix_mois ?? ''}
            onChange={(e) => onChange({ prix_mois: parseFloat(e.target.value) || 0 })}
            placeholder="0" className="h-7 text-[11px] mt-0" />
        </div>
        <div className="col-span-6">
          <label className="text-[10px] text-slate-400">Substitut terrain en pente (optionnel)</label>
          <Select
            value={piece.substitut_terrain_pente || '__empty__'}
            onValueChange={(v) => onChange({ substitut_terrain_pente: v === '__empty__' ? null : v })}
          >
            <SelectTrigger className="h-7 text-[11px] mt-0">
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__empty__">— Aucun —</SelectItem>
              {substituts.map((s, i) => (
                <SelectItem key={i} value={s.nom}>{s.nom}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

// ─── 🆕 [Phase 68] CompositionSection extrait au top-level pour éviter
//     le bug "focus perdu à chaque keystroke" : quand il était défini DANS
//     `TemplateRow`, chaque keystroke recréait le composant → React démontait
//     l'Input → focus perdu sur Qté/Select pièce. ─────────────────────────────
function CompositionSection({ title, listKey, placeholder, template, pieces, updateList }) {
  return (
    <div className="pt-2 border-t border-orange-100 space-y-1">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold text-orange-700">{title}</p>
        <Button
          size="sm"
          variant="outline"
          className="h-6 text-[10px] border-orange-200 hover:bg-orange-50"
          onClick={() => updateList(listKey, (l) => [...l, { piece: '__empty__', quantite_par_etage: 0, quantite: 0 }])}
        >
          <Plus className="w-2.5 h-2.5 mr-0.5" /> Pièce
        </Button>
      </div>
      {(template[listKey] || []).length === 0 ? (
        <p className="text-[10px] text-orange-400 italic">{placeholder}</p>
      ) : (
        <div className="space-y-1">
          {template[listKey].map((comp, cIdx) => (
            <div key={cIdx} className="flex items-center gap-1.5">
              <Input
                type="number"
                min="0"
                value={(comp.quantite_par_etage ?? comp.quantite) || ''}
                onChange={(e) => updateList(listKey, (l) => {
                  const next = [...l];
                  const v = parseInt(e.target.value, 10) || 0;
                  next[cIdx] = { ...next[cIdx], quantite_par_etage: v, quantite: v };
                  return next;
                })}
                className="h-7 text-xs w-14" placeholder="Qté"
              />
              <Select
                value={comp.piece || '__empty__'}
                onValueChange={(v) => updateList(listKey, (l) => {
                  const next = [...l];
                  next[cIdx] = { ...next[cIdx], piece: v };
                  return next;
                })}
              >
                <SelectTrigger className="h-7 text-xs flex-1"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__empty__">— Choisir —</SelectItem>
                  {pieces.filter((p) => p.nom).map((p, i) => (
                    <SelectItem key={i} value={p.nom}>{p.nom} ({p.categorie || '—'})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400"
                onClick={() => updateList(listKey, (l) => l.filter((_, i) => i !== cIdx))}>
                <X className="w-3 h-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Ligne d'un template d'étage ────────────────────────────────────
function TemplateRow({ template, pieces, onChange, onDelete }) {
  const updateList = (key, fn) => onChange({ [key]: fn(template[key] || []) });

  return (
    <div className="bg-white p-3 rounded-lg border border-orange-300 space-y-2">
      <div className="grid grid-cols-12 gap-2 items-end">
        <div className="col-span-5">
          <label className="text-[11px] text-slate-600 font-medium">Nom du type</label>
          <Input value={template.type || ''} onChange={(e) => onChange({ type: e.target.value })}
            placeholder="Standard 5×5" className="h-8 text-xs mt-0.5" />
        </div>
        <div className="col-span-3">
          <label className="text-[11px] text-slate-600 font-medium">Dimensions</label>
          <Input value={template.dimensions || ''} onChange={(e) => onChange({ dimensions: e.target.value })}
            placeholder="5′ × 5′" className="h-8 text-xs mt-0.5" />
        </div>
        <div className="col-span-3">
          <label className="text-[11px] text-slate-600 font-medium">Qté dispo</label>
          <Input type="number" min="0" value={template.quantite_disponible === 0 ? '' : (template.quantite_disponible || '')}
            onChange={(e) => onChange({ quantite_disponible: parseInt(e.target.value, 10) || 0 })}
            className="h-8 text-xs mt-0.5" />
        </div>
        <div className="col-span-1 flex justify-end">
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={onDelete}>
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>

      <CompositionSection
        template={template}
        pieces={pieces}
        updateList={updateList}
        title="Composition d'1 étage standard (intermédiaire)"
        listKey="composition"
        placeholder="Ex : 2 Cadres 5×5 + 4 Croisillons + 3 Madriers"
      />
      <CompositionSection
        template={template}
        pieces={pieces}
        updateList={updateList}
        title="+ Add-ons premier étage (base)"
        listKey="addon_premier"
        placeholder="Ex : 4 Vérins de pied"
      />
      <CompositionSection
        template={template}
        pieces={pieces}
        updateList={updateList}
        title="+ Add-ons dernier étage"
        listKey="addon_dernier"
        placeholder="Ex : 2 Garde-corps + 2 Plinthes"
      />
      <CompositionSection
        template={template}
        pieces={pieces}
        updateList={updateList}
        title="+ Add-ons accès (dernier étage)"
        listKey="addon_acces"
        placeholder="Ex : 1 Échelle d'accès"
      />
    </div>
  );
}
